// Constants
import { ETagVersionRollbackError } from "@ms/uno-constants/lib/local/ErrorConstants";
import { StoreEntityState } from "@ms/uno-constants/lib/local/StoreConstants";
// Entity
import { ExistenceStatus } from "@ms/uno-entitycore/lib/local/EntityStatus";
// Models
import { DeltaType } from "@ms/planner-models/lib/local/client/deltaSync/Enumerations";
// Tables
import { EntityTable, registerIndex } from "@ms/uno-entitycore/lib/local/table/EntityTable";
// Slices
import { AbstractSlice, TrackedOperation } from "./AbstractSlice";
import isEmpty from "lodash/isEmpty";
import { Temporal } from "temporal-polyfill-lite";
import { AbsentStoreEntityStatus, getStateBasedOnResponse, updateStatus } from "../utilities/StatusUtilities";
/**
 * Enhanced slice implementation for diffable types T that extends IDiffable<T>.
 * Extends the basic Slice with update and baseline functionality.
 * Uses EntityTable instead of BasicTable for diffing support.
 */ export class UpdatableSlice extends AbstractSlice {
    table() {
        return this.itemTable;
    }
    get(id, readBaseline) {
        const entity = this.table().get(id);
        const status = this.statusTable.get(id) ?? AbsentStoreEntityStatus;
        return {
            data: entity ? readBaseline ? entity.baseline : entity.value : null,
            status
        };
    }
    getCollection(collectionDescriptor, readBaseline) {
        const rowIds = this.table().index(collectionDescriptor.name).partition(collectionDescriptor.value).itemIds();
        const items = [];
        for (const rowId of rowIds){
            const entity = this.table().get(rowId);
            if (entity) {
                items.push(readBaseline ? entity.baseline : entity.value);
            }
        }
        const statusTable = this.collectionStatusTables[collectionDescriptor.name];
        const status = statusTable?.get(collectionDescriptor.value) ?? AbsentStoreEntityStatus;
        return {
            data: items,
            status
        };
    }
    createTable() {
        const enableHistoryTracking = this.enableUndoRedo;
        const table = new EntityTable(this.name, {
            enableHistoryTracking
        });
        for (const indexName of Object.keys(this.indexDefinitions)){
            const indexDefinition = this.indexDefinitions[indexName];
            registerIndex(table, indexName, indexDefinition, {
                enableHistoryTracking
            });
        }
        return table;
    }
    handleInput(id, item, overrideVersionCheck, isCoAuthDelta = false) {
        const currentEntity = this.table().get(id);
        if (!currentEntity) {
            this.table().add(id, item);
            return true;
        }
        // The local baseline cannot be trusted when either (a) the service signaled an etag scheme rollback
        // on this successful fetch (overrideVersionCheck), or (b) a prior 410 Gone on delta sync marked the
        // entity Dirty. In both cases accept the service value unconditionally, bypassing the isNewer
        // comparison. The header-driven override applies whenever the service asks for it; the Dirty-bit
        // path remains gated by EnableDirtyBitCheck.
        const overrideLocalBaseline = overrideVersionCheck || this.configProvider().flights.EnableDirtyBitCheck && this.statusTable.get(id)?.state === StoreEntityState.Dirty;
        if (overrideLocalBaseline || this.isNewer(currentEntity.baseline, item)) {
            currentEntity.updateBaseline(item, isCoAuthDelta);
            return true;
        }
        return false;
    }
    /**
     * Handles the start of an optimistic update by computing the real delta against the current value
     * and pushing it onto the entity so the change is reflected immediately.
     * @param id The ID of the entity being updated
     * @param updateKey The key identifying this optimistic delta
     * @param delta The proposed changes to apply
     * @param promotedPropertyOptimisticCalculator Optional calculator for deriving promoted-property deltas from the change
     * @returns True if the slice was changed, false otherwise
     */ handleUpdateStarted(id, updateKey, delta, promotedPropertyOptimisticCalculator) {
        const sliceCopy = this.table().get(id);
        if (!sliceCopy) {
            return false;
        }
        if (sliceCopy.status === ExistenceStatus.SoftDeleted || this.enableUndoRedo && sliceCopy.status === ExistenceStatus.ServerDeleted) {
            // If the entity is soft deleted or server deleted, we cannot update it.
            return false;
        }
        if (!delta) {
            return false;
        }
        let realDelta = sliceCopy.value.getDiff(sliceCopy.value.applyDiffs(delta));
        if (isEmpty(realDelta)) {
            return false;
        }
        if (promotedPropertyOptimisticCalculator) {
            const promotedDelta = promotedPropertyOptimisticCalculator(sliceCopy.value, realDelta);
            realDelta = {
                ...realDelta,
                ...promotedDelta
            };
        }
        sliceCopy.pushDelta(realDelta, updateKey);
        return true;
    }
    /**
     * Handles a failed update by reverting the optimistic delta and updating the entity's status.
     * On an ETagVersionRollback error (HTTP 412, gated by EnableDirtyBitCheck) the entity is marked
     * Dirty so the next fetch overwrites the local baseline unconditionally; otherwise the status is
     * derived from the response.
     * @param id The ID of the entity whose update failed
     * @param updateKey The key identifying the optimistic delta to revert
     * @param response The failure response, or null if none is available
     * @param errorCode Optional error code used to detect an ETag version rollback
     * @returns True if the slice was changed, false otherwise
     */ handleUpdateFailed(id, updateKey, response, errorCode) {
        let sliceChanged = false;
        const sliceCopy = this.table().get(id);
        if (!sliceCopy || sliceCopy.status === ExistenceStatus.SoftDeleted || this.enableUndoRedo && sliceCopy.status === ExistenceStatus.ServerDeleted) {
            // If the entity is soft deleted, server deleted, or does not exist, we cannot update it.
            return false;
        }
        if (sliceCopy.getDelta(updateKey)) {
            // If there is a delta for this update, we can revert it.
            sliceCopy.revertDelta(updateKey);
            sliceChanged = true;
        }
        // Update status if needed
        if (response) {
            // ETagVersionRollback (HTTP 412) means the service reset its etag scheme, so the local
            // baseline is no longer trustworthy. Mark the entity Dirty so the next fetch overwrites the
            // baseline unconditionally (see handleInput); the Dirty bit clears on the next successful
            // upsert (see upsertEntityAndStatus). Gated by EnableDirtyBitCheck.
            const isEtagRollback = this.configProvider().flights.EnableDirtyBitCheck && errorCode === ETagVersionRollbackError;
            const newState = isEtagRollback ? StoreEntityState.Dirty : getStateBasedOnResponse(response);
            if (newState) {
                sliceChanged = updateStatus(this.statusTable, id, {
                    state: newState
                }) || sliceChanged;
            }
        }
        return sliceChanged;
    }
    /**
     * Handles a successful update by committing the optimistic delta, folding it into the baseline
     * with the authoritative server value when provided.
     * @param id The ID of the entity that was updated
     * @param updateKey The key identifying the optimistic delta to commit
     * @param updatedItem The authoritative entity returned by the service, or undefined to commit the optimistic value
     * @returns True if the slice was changed, false otherwise
     */ handleUpdateSucceeded(id, updateKey, updatedItem, skipBaselineChain = false) {
        let sliceChanged = false;
        const sliceCopy = this.table().get(id);
        if (!sliceCopy || sliceCopy.status === ExistenceStatus.SoftDeleted || this.enableUndoRedo && sliceCopy.status === ExistenceStatus.ServerDeleted) {
            // If the entity is soft deleted, server deleted, or does not exist, we cannot update it.
            return false;
        }
        const delta = sliceCopy.getDelta(updateKey);
        if (delta) {
            delta.skipBaselineChainEntry = this.enableUndoRedo && skipBaselineChain;
            // If there is a delta for this update, we can apply it.
            sliceCopy.commitDelta(updateKey, updatedItem);
            sliceChanged = true;
        }
        return sliceChanged;
    }
    getBaselineChainPair(id, deltaKey) {
        if (!this.enableUndoRedo) {
            return undefined;
        }
        return this.table().get(id)?.getBaselineChainPair(deltaKey);
    }
    removeBaselineChainEntry(id, deltaKey, prunePrefix = false) {
        if (!this.enableUndoRedo) {
            return false;
        }
        const entity = this.table().get(id);
        if (!entity) {
            return false;
        }
        return entity.removeBaselineChainEntry(deltaKey, prunePrefix);
    }
    handleDeleteStarted(id) {
        const sliceCopy = this.table().get(id);
        if (!sliceCopy || sliceCopy.status === ExistenceStatus.SoftDeleted || this.enableUndoRedo && sliceCopy.status === ExistenceStatus.ServerDeleted) {
            // If the entity is soft deleted, server deleted, or does not exist, we cannot delete it.
            return false;
        }
        sliceCopy.softDelete();
        updateStatus(this.statusTable, id, {
            state: StoreEntityState.SoftDeleted
        });
        if (this.enableUndoRedo) {
            this.inFlightTrackedOperations.set(id, TrackedOperation.Delete);
        }
        return true;
    }
    handleDeleteFailed(id, response, errorCode) {
        const sliceCopy = this.table().get(id);
        if (!sliceCopy) {
            // If the entity does not exist, we don't need to do anything.
            return false;
        }
        let sliceChanged = false;
        if (this.enableUndoRedo && this.inFlightTrackedOperations.get(id) === TrackedOperation.Delete) {
            this.inFlightTrackedOperations.delete(id);
        }
        if (sliceCopy.status === ExistenceStatus.SoftDeleted) {
            // If the entity is already soft deleted, we can revert the soft delete.
            sliceCopy.undelete();
            updateStatus(this.statusTable, id, {
                state: StoreEntityState.Present
            });
            sliceChanged = true;
        }
        // ETagVersionRollback (HTTP 412) means the service reset its etag scheme, so the local
        // baseline is no longer trustworthy. Mark the entity Dirty so the next fetch overwrites the
        // baseline unconditionally (see handleInput); the Dirty bit clears on the next successful
        // upsert (see upsertEntityAndStatus). Gated by EnableDirtyBitCheck. Otherwise, map the
        // response to a new state. The rollback check sits outside the response guard so a
        // null-response rollback still marks the entity Dirty (mirrors AbstractSlice).
        const isEtagRollback = this.configProvider().flights.EnableDirtyBitCheck && errorCode === ETagVersionRollbackError;
        const newState = isEtagRollback ? StoreEntityState.Dirty : response ? getStateBasedOnResponse(response) : undefined;
        if (newState) {
            sliceChanged = updateStatus(this.statusTable, id, {
                state: newState
            }) || sliceChanged;
        }
        return sliceChanged;
    }
    handleDeleteSucceeded(id) {
        if (!this.enableUndoRedo) {
            return super.handleDeleteSucceeded(id);
        }
        const sliceCopy = this.table().get(id);
        if (!sliceCopy) {
            return false;
        }
        if (this.inFlightTrackedOperations.get(id) === TrackedOperation.Delete) {
            this.inFlightTrackedOperations.delete(id);
        }
        /** Entity is marked as ServerDeleted to be used by undo.
         *  Collection deltaSync echoes back every change after last sync, including our own
         *  removals. We need to continue retaining the entity so that undo can use it.
         */ if (sliceCopy.status === ExistenceStatus.ServerDeleted) {
            return false;
        }
        /** Acknowledgement of a delete the local user issued. If the entity is no longer SoftDeleted,
         * the user undid the delete while the request was still in flight (undelete restored it to
         * Created); this success is now stale, so leave the restored entity untouched. Co-author
         * deletes never reach here -- they flow through handleServerObservedDelete.
         */ if (sliceCopy.status !== ExistenceStatus.SoftDeleted) {
            return false;
        }
        return this.markServerConfirmedDelete(sliceCopy, id);
    }
    handleServerObservedDelete(id) {
        if (!this.enableUndoRedo) {
            return super.handleServerObservedDelete(id);
        }
        const sliceCopy = this.table().get(id);
        if (!sliceCopy) {
            return false;
        }
        /** Entity is already retained as ServerDeleted for a pending undo. Collection delta sync
         *  echoes our own delete back after it committed; retain the entity so undo can still
         *  recover it instead of evicting it via the co-author fallback below.
         */ if (sliceCopy.status === ExistenceStatus.ServerDeleted) {
            return false;
        }
        /** Delete surfaced from the server (fetch reconciliation or delta sync). If the local user
         * never soft-deleted it, a co-author removed a live entity -- remove it outright since there
         * is no local delete to keep recoverable.
         */ if (sliceCopy.status !== ExistenceStatus.SoftDeleted) {
            // by the time co-auth delta came in, entity is no longer soft deleted which means user did an undo of delete.
            // so this is a stale delta sync echo of the original delete op that was undone. Retain the entity and to avoid undoing the undo
            if (this.inFlightTrackedOperations.get(id) === TrackedOperation.Undelete) {
                return false;
            }
            return super.handleServerObservedDelete(id);
        }
        return this.markServerConfirmedDelete(sliceCopy, id);
    }
    /**
     * Record that the server has confirmed a delete for a locally soft-deleted entity: keep it in
     * the table marked ServerDeleted so undo can still recover it, and mark its status Absent.
     */ markServerConfirmedDelete(entity, id) {
        entity.markServerDeleted();
        updateStatus(this.statusTable, id, {
            state: StoreEntityState.Absent,
            timestamp: Temporal.Now.instant()
        });
        return true;
    }
    handleUndeleteStarted(id, localItem) {
        if (!this.enableUndoRedo) {
            return super.handleUndeleteStarted(id, localItem);
        }
        const sliceCopy = this.table().get(id);
        if (!sliceCopy) {
            return false;
        }
        /** entity could be in soft deleted or server deleted, in both cases undelete should work
         *  mark the entity status as created so it shows up in the UI, undelete does that.
         *  update the status table that the entity is now present.
         */ if (sliceCopy.status === ExistenceStatus.SoftDeleted || sliceCopy.status === ExistenceStatus.ServerDeleted) {
            sliceCopy.undelete();
            updateStatus(this.statusTable, id, {
                isFetching: false,
                state: StoreEntityState.Present,
                timestamp: Temporal.Now.instant()
            });
            this.inFlightTrackedOperations.set(id, TrackedOperation.Undelete);
            return true;
        }
        return false;
    }
    handleUndeleteSucceeded(id, restoredItem) {
        if (this.enableUndoRedo && this.inFlightTrackedOperations.get(id) === TrackedOperation.Undelete) {
            this.inFlightTrackedOperations.delete(id);
        }
        return super.handleUndeleteSucceeded(id, restoredItem);
    }
    handleCreateStarted(localId, localItem) {
        const sliceChanged = super.handleCreateStarted(localId, localItem);
        if (this.enableUndoRedo && sliceChanged) {
            this.inFlightTrackedOperations.set(localId, TrackedOperation.Create);
        }
        return sliceChanged;
    }
    handleCreateSucceeded(localId, createdItem, serverId) {
        const sliceCopy = this.table().get(localId);
        if (!sliceCopy) {
            return false;
        }
        if (this.enableUndoRedo && this.inFlightTrackedOperations.get(localId) === TrackedOperation.Create) {
            this.inFlightTrackedOperations.delete(localId);
        }
        // When undo/redo is enabled, a SoftDeleted status means the create was optimistically undone,
        // so we keep the entity soft deleted and skip updating the baseline with the server version.
        const isOptimisticallyUndone = this.enableUndoRedo && sliceCopy.status === ExistenceStatus.SoftDeleted;
        if (!isOptimisticallyUndone) {
            // Update the baseline with the server version
            sliceCopy.updateBaseline(createdItem);
        }
        // If the created item ID is different from the local ID, we need to re-key the entity
        if (serverId !== localId) {
            // Carry any still-pending marker (e.g. a "delete" from an optimistic undo-of-create) to the server id.
            const trackedOperation = this.enableUndoRedo ? this.inFlightTrackedOperations.get(localId) : undefined;
            this.table().reKey(localId, serverId);
            this.deleteEntityAndStatus(localId);
            if (trackedOperation !== undefined) {
                this.inFlightTrackedOperations.set(serverId, trackedOperation);
            }
        }
        updateStatus(this.statusTable, serverId, {
            state: isOptimisticallyUndone ? StoreEntityState.SoftDeleted : StoreEntityState.Present
        });
        return true;
    }
    handleSuspendDelta(id, deltaKey) {
        if (!this.enableUndoRedo) {
            return false;
        }
        const sliceCopy = this.table().get(id);
        if (!sliceCopy) {
            return false;
        }
        const delta = sliceCopy.getDelta(deltaKey);
        if (!delta || delta.suspended) {
            return false;
        }
        sliceCopy.suspendDelta(deltaKey);
        return true;
    }
    handleResumeDelta(id, deltaKey) {
        if (!this.enableUndoRedo) {
            return false;
        }
        const sliceCopy = this.table().get(id);
        if (!sliceCopy) {
            return false;
        }
        const delta = sliceCopy.getDelta(deltaKey);
        if (!delta?.suspended) {
            return false;
        }
        sliceCopy.resumeDelta(deltaKey);
        return true;
    }
    handleDropSuspendedDelta(id, deltaKey) {
        if (!this.enableUndoRedo) {
            return false;
        }
        const sliceCopy = this.table().get(id);
        if (!sliceCopy) {
            return false;
        }
        const delta = sliceCopy.getDelta(deltaKey);
        if (!delta?.suspended) {
            return false;
        }
        sliceCopy.revertDelta(deltaKey);
        return true;
    }
    /**
     * Helper method to add an entity and its status
     */ upsertEntityAndStatus(id, item, overrideVersionCheck) {
        let sliceChanged = false;
        sliceChanged = this.handleInput(id, item, overrideVersionCheck, false) || sliceChanged;
        sliceChanged = updateStatus(this.statusTable, id, {
            state: StoreEntityState.Present
        }) || sliceChanged;
        return sliceChanged;
    }
    /**
     * Handles a single delta sync item based on its type, including Update operations.
     * Extends the base implementation to support Update delta type.
     * @param itemId The ID of the item
     * @param item The delta entity containing the operation type and payload
     * @returns True if the slice was changed, false otherwise
     */ handleDeltaSyncItem(itemId, item) {
        // A Create delta sync echo can resurrect the entity (it flips status to Present), if an undo of create i.e. delete was done
        // if create [self echo of own create] / delete [undo of create] op is in flight, defer the delta sync echo
        // undefined existence status - delta sync echo can be accepted as there is no in flight op, genuine create
        // undelete existence status - delta sync echo can be accepted as entity is anyways Present, just re-affirms existence. [Stale ones are dropped via isNewer check]
        if (this.enableUndoRedo && item.type === DeltaType.Create) {
            const trackedOperation = this.inFlightTrackedOperations.get(itemId);
            if (trackedOperation === TrackedOperation.Create || trackedOperation === TrackedOperation.Delete) {
                return false;
            }
        }
        switch(item.type){
            case DeltaType.Update:
                return this.handleDeltaSyncUpdate(itemId, item.entity);
            default:
                return super.handleDeltaSyncItem(itemId, item);
        }
    }
    /**
     * Handles a delta sync update for a specific entity.
     * @param id The ID of the entity to update.
     * @param item The delta entity containing the update information.
     * @returns True if the update was successful, false otherwise.
     */ handleDeltaSyncUpdate(id, item) {
        const sliceCopy = this.table().get(id);
        if (!sliceCopy) {
            // If the entity does not exist, we cannot update it.
            return false;
        }
        return this.handleInput(id, sliceCopy.baseline.applyDiffs(item), undefined, true);
    }
    constructor(name, indexDefinitions, isNewer, configProvider){
        super(name, indexDefinitions, configProvider), this.isNewer = isNewer, this.itemTable = this.createTable();
    }
}
